import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { isWorkHubPath, visibleWorkHubItems } from "@/pages/work-hub/workHub";
import { LayoutDashboard, Users, UserCircle, CheckSquare, Zap, BarChart3, Settings, LogOut, Menu, X, User, CalendarDays, Building2, ChevronDown, Check, Key, Plug, Clock, Fingerprint, UserCog, ClipboardList, Wallet, FileText, Cog, ChevronRight, CalendarOff, Shield, FileBarChart, PartyPopper, CreditCard, Package, AlertTriangle, Lock, Receipt, PanelLeftClose, PanelLeftOpen, ShoppingCart, FileCheck, ArrowDownToLine, ArrowUpFromLine, RotateCcw, FileMinus, Truck, FilePlus, AudioLines, MessageCircle, MapPinned, Briefcase, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useBusiness } from "@/contexts/BusinessContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Light mode logo (dark text for light background)
const LOGO_LIGHT_URL = "/logo-light.png";
// Dark mode logo (white text for dark background)
const LOGO_DARK_URL = "/logo-dark.png";

const Layout = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { selectedBusiness, selectBusiness, clearBusiness } = useBusiness();
  
  // Force light mode for authenticated app
  useEffect(() => {
    setTheme("light");
  }, [setTheme]);
  
  // State for business switcher and user profile
  const [currentUser, setCurrentUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  // Fetch user info and businesses on mount
  useEffect(() => {
    fetchCurrentUser();
    fetchBusinesses();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/my-businesses`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object with businesses key
        const businessList = Array.isArray(data) ? data : (data.businesses || []);
        setBusinesses(businessList);
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
  };

  const handleBusinessSwitch = (business) => {
    selectBusiness(business);
    setSwitcherOpen(false);
    toast.success(`Switched to ${business.name}`);
  };

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const canSwitchBusiness = currentUser?.can_switch_business || isSuperAdmin;

  // Subscription context
  const { subscription, status: subStatus, inGracePeriod, graceDaysLeft, daysUntilExpiry, hasModuleAccess } = useSubscription();
  
  // Get effective permissions for the current user
  const effectivePermissions = currentUser?.effective_permissions || [];

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      // Clear the selected business on logout
      clearBusiness();
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const location = useLocation();
  const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(false);
  const [attendanceAccess, setAttendanceAccess] = useState([]);

  // Fetch attendance access permissions
  useEffect(() => {
    const fetchAttendanceAccess = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/attendance/my-access`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setAttendanceAccess(data.pages || []);
        }
      } catch (error) {
        console.error("Error fetching attendance access:", error);
      }
    };
    if (currentUser) {
      fetchAttendanceAccess();
    }
  }, [currentUser]);

  // Attendance sub-menu items with page IDs for access control
  const attendanceSubItems = [
    { to: "/attendance", icon: LayoutDashboard, label: "Dashboard", pageId: "attendance_dashboard" },
    { to: "/attendance/punch", icon: Fingerprint, label: "Punch In/Out", pageId: "attendance_punch" },
    { to: "/attendance/leave", icon: CalendarOff, label: "Leave Management", pageId: "attendance_leave" },
    { to: "/attendance/holidays", icon: PartyPopper, label: "Holiday Calendar", adminOnly: true },
    { to: "/attendance/leave-policy", icon: Shield, label: "Leave Policy", adminOnly: true },
    { to: "/attendance/leave-reports", icon: FileBarChart, label: "Leave Reports", adminOnly: true },
    { to: "/attendance/staff", icon: UserCog, label: "Staff Profiles", pageId: "attendance_staff" },
    { to: "/attendance/records", icon: ClipboardList, label: "Attendance Records", pageId: "attendance_records" },
    { to: "/attendance/salary", icon: Wallet, label: "Salary Management", pageId: "attendance_salary" },
    { to: "/attendance/reports", icon: FileText, label: "Reports", pageId: "attendance_reports" },
    { to: "/attendance/location-tracking", icon: MapPinned, label: "Location Tracking", permission: "location_tracking" },
    { to: "/attendance/staff-app", icon: Smartphone, label: "Staff App Access", roles: ["super_admin", "admin", "manager"] },
    { to: "/attendance/settings", icon: Cog, label: "Settings", pageId: "attendance_settings", adminOnly: true },
  ];

  // Filter attendance sub-items based on access control
  const filteredAttendanceSubItems = attendanceSubItems.filter(subItem => {
    // Role-gated items (e.g. Staff App Access: admins + managers decide app features for their staff).
    if (subItem.roles) return subItem.roles.includes(currentUser?.role);
    // Permission-gated items (sensitive data): admins, or users explicitly granted the page.
    if (subItem.permission) {
      return ["super_admin", "admin"].includes(currentUser?.role) || (currentUser?.effective_permissions || []).includes(subItem.permission);
    }
    // Admin-only items are handled separately
    if (subItem.adminOnly) {
      return currentUser?.role && ["super_admin", "admin"].includes(currentUser.role);
    }
    // Super admin always has access
    if (currentUser?.role === "super_admin") return true;
    // Check access control list
    if (subItem.pageId && attendanceAccess.length > 0) {
      return attendanceAccess.includes(subItem.pageId);
    }
    return true;
  });

  // Check if any attendance route is active
  const isAttendanceActive = location.pathname.startsWith("/attendance");

  // Billing + Proposals modules removed. /api/billing/settings stays live
  // (salary slips read the company profile / letterhead from it).

  // Subscription menu state (super_admin only)
  const [subscriptionMenuOpen, setSubscriptionMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("main_sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("main_sidebar_collapsed", sidebarCollapsed ? "1" : "0"); } catch {} }, [sidebarCollapsed]);
  const isSubscriptionActive = location.pathname.startsWith("/subscriptions");

  const subscriptionSubItems = isSuperAdmin ? [
    { to: "/subscriptions", icon: CreditCard, label: "Subscriptions" },
    { to: "/subscriptions/plans", icon: Package, label: "Manage Plans" },
  ] : [
    { to: "/subscriptions", icon: CreditCard, label: "Subscriptions" },
  ];

  // All navigation items with their page identifiers
  // Dashboard is first, then Attendance menu is rendered separately below it
  const allNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", page: "dashboard" },
    { to: "/leads", icon: Users, label: "Leads", page: "leads", moduleKey: "leads" },
    { to: "/contacts", icon: UserCircle, label: "Contacts", page: "contacts", moduleKey: "contacts" },
    { to: "/work-hub", icon: Briefcase, label: "Work Hub", page: "__work_hub", group: "work_hub" },
    { to: "/calendar", icon: CalendarDays, label: "Follow Up", page: "calendar", moduleKey: "calendar" },
    { to: "/reports", icon: BarChart3, label: "Reports", page: "reports", moduleKey: "reports" },
    { to: "/ai-conversations", icon: AudioLines, label: "AI Conversation", page: "ai_conversations" },
    { to: "/whatsapp", icon: MessageCircle, label: "WhatsApp", page: "whatsapp" },
    { to: "/integrations", icon: Plug, label: "Integrations", page: "integrations", moduleKey: "integrations" },
    { to: "/settings", icon: Settings, label: "Settings", page: "settings" },
    ...(isSuperAdmin ? [{ to: "/super-admin/businesses", icon: Building2, label: "Business Accounts", page: "super_admin" }] : []),
  ];
  
  // Work Hub: collapsible group of client-work pages, filtered by page permission.
  const workHubItems = visibleWorkHubItems(currentUser);
  const inWorkHub = isWorkHubPath(location.pathname);
  const [workHubOpen, setWorkHubOpen] = useState(() => { try { return localStorage.getItem("nav_work_hub_open") !== "0"; } catch { return true; } });
  useEffect(() => { if (inWorkHub) setWorkHubOpen(true); }, [inWorkHub]);
  useEffect(() => { try { localStorage.setItem("nav_work_hub_open", workHubOpen ? "1" : "0"); } catch {} }, [workHubOpen]);

  const renderWorkHub = (mobile) => (
    <div key="work-hub">
      <button
        onClick={() => { if (!mobile && sidebarCollapsed) navigate("/work-hub"); else setWorkHubOpen((v) => !v); }}
        data-testid={mobile ? "nav-work-hub-mobile" : "nav-work-hub"}
        aria-expanded={workHubOpen}
        title="Work Hub"
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
          inWorkHub
            ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
            : "text-slate-600 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
        }`}
      >
        <span className="flex items-center gap-3">
          <Briefcase className="w-5 h-5" />
          <span>Work Hub</span>
        </span>
        <ChevronDown className={`attendance-chevron w-4 h-4 transition-transform ${workHubOpen ? "rotate-180" : ""}`} />
      </button>
      {workHubOpen && (
        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-indigo-200 dark:border-indigo-800/70 pl-3">
          {workHubItems.map((sub) => {
            const locked = !isSuperAdmin && sub.moduleKey && !hasModuleAccess(sub.moduleKey);
            if (locked) {
              return (
                <div key={sub.to} title={`${sub.label} — not included in your plan`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 dark:text-slate-600 cursor-not-allowed select-none">
                  <sub.icon className="w-4 h-4" /><span>{sub.label}</span><Lock className="w-3 h-3 ml-auto" />
                </div>
              );
            }
            return (
              <NavLink key={sub.to} to={sub.to} end={sub.end}
                onClick={() => mobile && setMobileMenuOpen(false)}
                data-testid={`nav-work-hub-${sub.label.toLowerCase().replace(/\s+/g, "-")}${mobile ? "-mobile" : ""}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    isActive
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 font-medium before:absolute before:-left-[14px] before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-indigo-500"
                      : "text-slate-500 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  }`
                }
              >
                <sub.icon className="w-4 h-4" />
                <span>{sub.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );

  // Profile is always visible
  const profileItem = { to: "/profile", icon: User, label: "My Profile", page: "profile" };
  
  // Filter nav items based on permissions (super_admin/admin sees all, others based on effective_permissions)
  const navItems = (isSuperAdmin || isAdmin)
    ? [...allNavItems, profileItem]
    : [...allNavItems.filter(item => {
        if (item.adminManagerOnly) return isManager; // admin/super_admin handled above
        if (item.group === "work_hub") return workHubItems.length > 0;
        return effectivePermissions.includes(item.page);
      }), profileItem];

  // Use dark logo when in dark mode, light logo for light mode
  const currentLogo = theme === "dark" ? LOGO_DARK_URL : LOGO_LIGHT_URL;
  // Sidebar is always dark now — always use the dark-variant logo inside it.
  const sidebarLogo = LOGO_DARK_URL;
  // Collapsed sidebar shows the compact mark instead of the full wordmark.
  const collapsedSidebarLogo = "/logo-collapsed.jpeg";

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-100 via-slate-50 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Mobile Header with Hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <div className="w-32">
            <img src={currentLogo} alt="Dnexus Media Logo" className="w-full h-auto object-contain" />
          </div>
          {/* Mobile Profile Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="mobile-user-profile-button"
                className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {currentUser?.picture ? (
                  <img 
                    src={currentUser.picture} 
                    alt={currentUser.name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-indigo-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
                <p className="text-sm font-medium">{currentUser?.name}</p>
                <p className="text-xs text-slate-500 truncate">{currentUser?.email}</p>
              </div>
              <DropdownMenuItem 
                onClick={() => navigate("/profile")}
                className="cursor-pointer"
              >
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate("/profile?tab=password")}
                className="cursor-pointer"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`dark md:hidden fixed left-0 top-0 w-64 h-full bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col overflow-hidden ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="w-full max-w-[150px]">
              <img src={sidebarLogo} alt="Dnexus Media Logo" className="w-full h-auto object-contain" />
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
          {/* Mobile Business Switcher */}
          {selectedBusiness && isAdmin && businesses.length > 1 && (
            <div className="mt-4">
              <select
                value={selectedBusiness?.business_id || ""}
                onChange={(e) => {
                  const business = businesses.find(b => b.business_id === e.target.value);
                  if (business) handleBusinessSwitch(business);
                }}
                data-testid="mobile-business-switcher"
                className="w-full px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-xs font-medium text-indigo-700 dark:text-indigo-300 border-none focus:ring-2 focus:ring-indigo-500"
              >
                {businesses.map((business) => (
                  <option key={business.business_id} value={business.business_id}>
                    {business.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedBusiness && (!isAdmin || businesses.length <= 1) && (
            <div className="mt-4 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300 truncate">
                  {selectedBusiness.name}
                </span>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Dashboard - First Item */}
          <NavLink
            to="/dashboard"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
                  : "text-slate-600 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              }`
            }
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </NavLink>
          
          {/* Attendance Menu - Right After Dashboard */}
          {filteredAttendanceSubItems.length > 0 && (
            <div>
              <button
                onClick={() => setAttendanceMenuOpen(!attendanceMenuOpen)}
                data-testid="nav-attendance-mobile"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  isAttendanceActive
                    ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-semibold"
                    : "text-slate-600 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5" />
                  <span>Attendance</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${attendanceMenuOpen ? "rotate-90" : ""}`} />
              </button>
              
              {attendanceMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-emerald-200 dark:border-emerald-800 pl-3">
                  {filteredAttendanceSubItems.map((subItem) => (
                    <NavLink
                      key={subItem.to}
                      to={subItem.to}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`nav-attendance-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium"
                            : "text-slate-500 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Remaining Nav Items (skip Dashboard as it's rendered above) */}
          {navItems.filter(item => item.page !== "dashboard").map((item) => {
            if (item.group === "work_hub") return renderWorkHub(true);
            const locked = !isSuperAdmin && item.moduleKey && !hasModuleAccess(item.moduleKey);
            if (locked) {
              return (
                <div key={item.to} data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-300 dark:text-slate-600 cursor-not-allowed select-none"
                  title={`${item.label} — not included in your plan`}>
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  <Lock className="w-3.5 h-3.5 ml-auto" />
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
                      : "text-slate-600 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Subscription Menu - Admin & Super Admin (Mobile) */}
          {(isSuperAdmin || currentUser?.role === "admin") && (
            <div>
              <button
                onClick={() => setSubscriptionMenuOpen(!subscriptionMenuOpen)}
                data-testid="nav-subscriptions-mobile"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  isSubscriptionActive
                    ? "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-semibold"
                    : "text-slate-600 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5" />
                  <span>Subscriptions</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${subscriptionMenuOpen ? "rotate-90" : ""}`} />
              </button>
              {subscriptionMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-amber-200 dark:border-amber-800 pl-3">
                  {subscriptionSubItems.map((subItem) => (
                    <NavLink key={subItem.to} to={subItem.to}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`nav-sub-mobile-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-medium"
                            : "text-slate-500 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700">
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              handleLogout();
            }}
            data-testid="mobile-logout-button"
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-600 dark:text-white hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Desktop Floating Glass Sidebar */}
      <aside
        data-testid="sidebar"
        data-collapsed={sidebarCollapsed ? "true" : "false"}
        className={`dark fixed left-0 top-0 ${sidebarCollapsed ? "w-20" : "w-64"} m-4 rounded-2xl h-[calc(100vh-2rem)] bg-slate-900 backdrop-blur-md border border-slate-700/50 shadow-2xl z-50 hidden md:flex flex-col overflow-hidden transition-all duration-200 ${sidebarCollapsed ? "[&_nav_span]:hidden [&_nav_a]:!justify-center [&_nav_a]:!px-2 [&_nav_>div>button]:!justify-center [&_nav_>div>button]:!px-2 [&_nav_>div>button>span:first-child]:!gap-0 [&_nav_>div>div]:hidden [&_.attendance-chevron]:hidden [&_.subscription-chevron]:hidden [&_.business-switcher-block]:hidden [&_.logout-btn]:!justify-center [&_.logout-btn]:!px-2" : ""}`}
      >
        {/* Collapse toggle (desktop only) — floats top-right of sidebar */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="toggle-main-sidebar"
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-slate-100 z-10"
        >
          {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
        {/* Collapsed-mode mini logo — uses the compact "D" mark. */}
        {sidebarCollapsed && (
          <div className="p-4 flex justify-center border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
            <div className="w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center bg-white/5">
              <img src={collapsedSidebarLogo} alt="Dnexus" className="w-full h-full object-contain" style={{ imageRendering: 'crisp-edges' }} />
            </div>
          </div>
        )}
        <div className="business-switcher-block p-6 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-center">
            <div className="w-full max-w-[180px]">
              <img src={sidebarLogo} alt="Dnexus Media Logo" className="w-full h-auto object-contain" style={{ imageRendering: 'crisp-edges' }} />
            </div>
          </div>
          {/* Business Switcher for Super Admin / Indicator for others */}
          {selectedBusiness && (
            <div className="mt-4 relative" ref={switcherRef}>
              {canSwitchBusiness && businesses.length > 1 ? (
                <>
                  <button
                    onClick={() => setSwitcherOpen(!switcherOpen)}
                    data-testid="business-switcher-button"
                    className="w-full px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300 truncate" title={selectedBusiness.name}>
                          {selectedBusiness.name}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {/* Dropdown */}
                  {switcherOpen && (
                    <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Switch Business</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {businesses.map((business) => (
                          <button
                            key={business.business_id}
                            onClick={() => handleBusinessSwitch(business)}
                            data-testid={`switch-business-${business.business_id}`}
                            className={`w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors ${
                              selectedBusiness?.business_id === business.business_id ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                            }`}
                          >
                            <div className="flex flex-col items-start min-w-0">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate w-full text-left">{business.name}</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400">{business.city}</span>
                            </div>
                            {selectedBusiness?.business_id === business.business_id && (
                              <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300 truncate" title={selectedBusiness.name}>
                      {selectedBusiness.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Dashboard - First Item */}
          <NavLink
            to="/dashboard"
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
                  : "text-slate-600 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              }`
            }
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </NavLink>
          
          {/* Attendance Menu - Right After Dashboard */}
          {filteredAttendanceSubItems.length > 0 && (
            <div>
              <button
                onClick={() => setAttendanceMenuOpen(!attendanceMenuOpen)}
                data-testid="nav-attendance"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  isAttendanceActive
                    ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-semibold"
                    : "text-slate-600 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5" />
                  <span>Attendance</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${attendanceMenuOpen ? "rotate-90" : ""}`} />
              </button>
              
              {attendanceMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-emerald-200 dark:border-emerald-800 pl-3">
                  {filteredAttendanceSubItems.map((subItem) => (
                    <NavLink
                      key={subItem.to}
                      to={subItem.to}
                      data-testid={`nav-attendance-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium"
                            : "text-slate-500 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Remaining Nav Items (skip Dashboard as it's rendered above) */}
          {navItems.filter(item => item.page !== "dashboard").map((item) => {
            if (item.group === "work_hub") return renderWorkHub(false);
            const locked = !isSuperAdmin && item.moduleKey && !hasModuleAccess(item.moduleKey);
            if (locked) {
              return (
                <div key={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-300 dark:text-slate-600 cursor-not-allowed select-none"
                  title={`${item.label} — not included in your plan`}>
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  <Lock className="w-3.5 h-3.5 ml-auto" />
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold"
                      : "text-slate-600 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Subscription Menu - Admin & Super Admin (Desktop) */}
          {(isSuperAdmin || currentUser?.role === "admin") && (
            <div>
              <button
                onClick={() => setSubscriptionMenuOpen(!subscriptionMenuOpen)}
                data-testid="nav-subscriptions"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  isSubscriptionActive
                    ? "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-semibold"
                    : "text-slate-600 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5" />
                  <span>Subscriptions</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${subscriptionMenuOpen ? "rotate-90" : ""}`} />
              </button>
              {subscriptionMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-amber-200 dark:border-amber-800 pl-3">
                  {subscriptionSubItems.map((subItem) => (
                    <NavLink key={subItem.to} to={subItem.to}
                      data-testid={`nav-sub-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-medium"
                            : "text-slate-500 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Logout"
            className="logout-btn w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-600 dark:text-white hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className={sidebarCollapsed ? "hidden" : ""}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`ml-0 ${sidebarCollapsed ? "md:ml-28" : "md:ml-72"} min-h-screen transition-all duration-200`}>
        {/* Impersonation banner — shown when a super admin is logged in as a business */}
        {currentUser?.impersonated_by && (
          <div className="fixed top-0 right-0 left-0 md:left-72 z-50 bg-amber-500 text-white text-sm font-semibold flex items-center justify-center gap-3 py-2 px-4"
            style={sidebarCollapsed ? { left: undefined } : undefined} data-testid="impersonation-banner">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="truncate">You are logged in as <b>{currentUser?.name}</b> ({currentUser?.business_name || "this business"}) via Super Admin.</span>
            <button
              onClick={async () => {
                try {
                  const r = await fetch(`${BACKEND_URL}/api/super-admin/stop-impersonate`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}" });
                  if (r.ok) { clearBusiness(); window.location.href = "/super-admin/businesses"; }
                  else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Failed to return"); }
                } catch { toast.error("Failed to return"); }
              }}
              className="shrink-0 bg-white text-amber-700 rounded-full px-3 py-1 text-xs font-bold hover:bg-amber-50 transition-colors"
              data-testid="return-to-superadmin-btn"
            >
              Return to Super Admin
            </button>
          </div>
        )}
        {/* Top Header Bar */}
        <header className={`fixed top-0 right-0 left-0 ${sidebarCollapsed ? "md:left-28" : "md:left-72"} h-16 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-700/50 z-40 flex items-center justify-end px-4 md:px-8 transition-all duration-200`}>
          {/* Notification bell — unread badge + 20s polling + dropdown preview */}
          <NotificationBell />
          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-profile-button"
                className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                {currentUser?.picture ? (
                  <img 
                    src={currentUser.picture} 
                    alt={currentUser.name}
                    className="w-9 h-9 rounded-full object-cover border-2 border-indigo-200"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{currentUser?.name || "User"}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">{currentUser?.role || "user"}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
                <p className="text-sm font-medium">{currentUser?.name}</p>
                <p className="text-xs text-slate-500 truncate">{currentUser?.email}</p>
              </div>
              <DropdownMenuItem 
                onClick={() => navigate("/profile")}
                data-testid="profile-menu-item"
                className="cursor-pointer"
              >
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate("/profile?tab=password")}
                data-testid="change-password-menu-item"
                className="cursor-pointer"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                data-testid="logout-menu-item"
                className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        
        {/* Subscription Banner */}
        {!isSuperAdmin && subscription && (subStatus === "expired" || subStatus === "suspended" || inGracePeriod || (daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0)) && (
          <div className={`fixed top-16 right-0 left-0 md:left-72 z-30 px-4 md:px-8 py-2.5 text-sm font-medium flex items-center justify-center gap-2 ${
            subStatus === "suspended"
              ? "bg-slate-800 text-white"
              : subStatus === "expired"
                ? "bg-red-500 text-white"
                : inGracePeriod
                  ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
          }`} data-testid="subscription-banner">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {subStatus === "suspended" ? (
              <span>This business has been suspended. Contact your administrator.</span>
            ) : subStatus === "expired" ? (
              <span>Your subscription has expired. Contact your administrator to renew.</span>
            ) : inGracePeriod ? (
              <span>Your subscription has expired. Grace period ends in {graceDaysLeft} day{graceDaysLeft !== 1 ? "s" : ""}. Contact your administrator.</span>
            ) : (
              <span>Your subscription expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? "s" : ""}. Please renew soon.</span>
            )}
          </div>
        )}

        {/* Suspended Overlay */}
        {!isSuperAdmin && subStatus === "suspended" && (
          <div className="fixed inset-0 md:left-72 z-20 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm flex items-center justify-center" data-testid="suspended-overlay">
            <div className="text-center p-8 max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Lock className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Business Suspended</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Access to this business has been suspended. Please contact your administrator or support to resolve this.
              </p>
              <a href="mailto:info@dnexusmedia.com" className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-semibold hover:bg-amber-400 transition-colors">
                Contact Support
              </a>
            </div>
          </div>
        )}

        {/* Page Content */}
        <div className={`${!isSuperAdmin && subscription && (subStatus === "expired" || subStatus === "suspended" || inGracePeriod || (daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0)) ? "pt-28 md:pt-28" : "pt-20 md:pt-20"} p-3 md:p-5`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
